import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaCheck, FaPlus, FaMinus, FaTrash } from "react-icons/fa";
import type { MenuItem, Branch, VariantGroup } from "~/types/new_types";
import { getFormName } from "~/utils/menuItemHelper";

interface ItemCard {
  id: string;
  name: string;
  variants: Record<string, any>;
  quantity: number;
}

interface InlineVariantCustomizationProps {
  item: MenuItem;
  theme:
    | Branch["theme"]
    | {
        primaryColor?: string;
        secondaryColor?: string;
        backgroundColor?: string;
        cardColor?: string;
        textColor?: string;
        borderColor?: string;
        borderRadius?: string;
        fontFamily?: string;
      };
  itemCards: ItemCard[];
  selectedCardIndex: number;
  onVariantUpdate: (cardIndex: number, optionName: string, value: any) => void;
  onQuantityChange?: (cardIndex: number, newQuantity: number) => void;
}

/**
 * Helper function to get the storage key for a variant group
 */
const getVariantGroupKey = (group: VariantGroup): string => {
  if (group.id) return group.id;
  if (group.form_name) return group.form_name;
  return "unknown";
};

/**
 * Helper function to validate if selection meets minimum requirements
 */
const isValidSelection = (
  selected: any,
  minSelection: number,
  isMultiSelection: boolean
): boolean => {
  if (minSelection === 0) return true;

  if (isMultiSelection) {
    return Array.isArray(selected) && selected.length >= minSelection;
  }

  return selected !== null && selected !== undefined;
};

/**
 * Helper function to get current selection count
 */
const getSelectionCount = (
  selected: any,
  isMultiSelection: boolean
): number => {
  if (isMultiSelection) {
    return Array.isArray(selected) ? selected.length : 0;
  }
  return selected !== null && selected !== undefined ? 1 : 0;
};

/**
 * Helper function to get localized option field
 */
const getLocalizedField = (
  optionData: any,
  field: string,
  language: string
): string => {
  // Check if Arabic and ar object exists with the field
  if (language === "ar" && optionData?.ar?.[field]) {
    return optionData.ar[field];
  }
  // Fallback to default field
  return optionData?.[field] || "";
};

export const InlineVariantCustomization: React.FC<
  InlineVariantCustomizationProps
> = ({
  item,
  theme,
  itemCards,
  selectedCardIndex,
  onVariantUpdate,
  onQuantityChange,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [quantityInput, setQuantityInput] = useState<string>("");

  // Update quantity input when selected card changes
  React.useEffect(() => {
    const selectedCard = itemCards[selectedCardIndex];
    if (selectedCard) {
      setQuantityInput(selectedCard.quantity.toString());
    }
  }, [itemCards, selectedCardIndex]);

  // Process variants_and_options into a normalized structure
  const variantGroups = useMemo(() => {
    const groups: any[] = [];

    // Process meals_and_combos first
    if (item.meals_and_combos && Array.isArray(item.meals_and_combos)) {
      item.meals_and_combos.forEach((group) => {
        if (group && group.data) {
          const comboData = group.data as any;
          if (Array.isArray(comboData.items)) {
            groups.push({
              id: group.id || "",
              formName: getFormName(group, i18n.language) || "Combo",
              actionType: group.action_type || "upgrade",
              isRequired: Boolean(
                (group as any).min_selection && (group as any).min_selection > 0
              ),
              maxSelection: Number((group as any).max_selection) || 1,
              minSelection: Number((group as any).min_selection) || 0,
              isMultiSelection: Boolean((group as any).is_multiselection),
              isComboUpgrade: true,
              data: [
                {
                  name: getFormName(group, i18n.language) || "Combo",
                  price: Number(comboData.combo_price || comboData.price || 0),
                  is_active: true,
                  is_default: false,
                  note: `${comboData.items.length} items included`,
                  description: comboData.items
                    .map(
                      (item: any) =>
                        `${item.quantity || 1}x ${
                          item.item_name || item.name || "Item"
                        }`
                    )
                    .join(", "),
                  _isCombo: true,
                  _comboItems: comboData.items,
                },
              ],
            });
          }
        }
      });
    }

    // Process variants_and_options
    if (item.variants_and_options && Array.isArray(item.variants_and_options)) {
      item.variants_and_options.forEach((group, index) => {
        if (group && group.data) {
          // Handle different action types
          let processedData: any[] = [];
          let isComboUpgrade = false;

          if (Array.isArray(group.data)) {
            // Regular variant options (selection type)
            processedData = group.data;
          } else if (group.data && typeof group.data === "object") {
            // Check if this is combo data structure (has items and combo_price)
            const comboData = group.data as any;
            if (
              Array.isArray(comboData.items) &&
              comboData.combo_price !== undefined
            ) {
              isComboUpgrade = true;
              processedData = [
                {
                  name: getFormName(group, i18n.language) || "Combo Option",
                  price: Number(comboData.combo_price || 0),
                  is_active: true,
                  is_default: false,
                  note: `${comboData.items.length} items included`,
                  description: comboData.items
                    .map(
                      (item: any) =>
                        `${item.quantity || 1}x ${
                          item.item_name || item.name || "Item"
                        }`
                    )
                    .join(", "),
                  _isCombo: true,
                  _comboItems: comboData.items,
                },
              ];
            }
          }

          if (processedData.length > 0) {
            // Create a unique form name if there are duplicates
            const baseFormName = getFormName(group, i18n.language) || "Options";
            const existingNamesCount = groups.filter((g) =>
              g.formName.startsWith(baseFormName)
            ).length;
            const uniqueFormName =
              existingNamesCount > 0
                ? `${baseFormName} ${existingNamesCount + 1}`
                : baseFormName;

            const groupToAdd = {
              id: group.id || `group-${index}`,
              formName: uniqueFormName,
              actionType: group.action_type || "selection",
              isRequired: Boolean(
                (group as any).min_selection && (group as any).min_selection > 0
              ),
              maxSelection:
                Number((group as any).max_selection) ||
                ((group as any).is_multiselection ? 10 : 1),
              minSelection: Number((group as any).min_selection) || 0,
              isMultiSelection: Boolean((group as any).is_multiselection),
              isComboUpgrade,
              data: processedData,
            };
            groups.push(groupToAdd);
          }
        }
      });
    }

    return groups;
  }, [item.variants_and_options, item.meals_and_combos]);

  const handleQuantityDecrease = () => {
    const numericValue = parseInt(quantityInput, 10);
    if (!isNaN(numericValue) && numericValue > 1) {
      const newQuantity = numericValue - 1;
      setQuantityInput(newQuantity.toString());
      if (
        onQuantityChange &&
        selectedCardIndex !== null &&
        selectedCardIndex >= 0
      ) {
        onQuantityChange(selectedCardIndex, newQuantity);
      }
    }
  };

  const handleQuantityIncrease = () => {
    const numericValue = parseInt(quantityInput, 10);
    const newQuantity = isNaN(numericValue) ? 2 : numericValue + 1;
    setQuantityInput(newQuantity.toString());
    if (
      onQuantityChange &&
      selectedCardIndex !== null &&
      selectedCardIndex >= 0
    ) {
      onQuantityChange(selectedCardIndex, newQuantity);
    }
  };

  // Check if we have legacy options or new variants_and_options
  const hasNewVariants = variantGroups.length > 0;

  const selectedCard = itemCards[selectedCardIndex];

  // Auto-select default options when a card is first created (has no variants selected)
  React.useEffect(() => {
    if (!selectedCard || !hasNewVariants) return;

    // Check if the card already has variants selected
    const hasExistingSelections = Object.keys(selectedCard.variants || {}).some(
      (key) => {
        const value = selectedCard.variants[key];
        if (Array.isArray(value)) return value.length > 0;
        return value !== null && value !== undefined;
      }
    );

    // Only auto-select defaults if no selections exist yet
    if (hasExistingSelections) return;

    // Go through each variant group and select default options
    variantGroups.forEach((group) => {
      const groupKey = group.id;
      const groupOptions = Array.isArray(group.data) ? group.data : [];

      // Find options with is_default: true
      const defaultOptions = groupOptions.filter(
        (option: any) => option.is_default === true && option.is_active !== false
      );

      if (defaultOptions.length > 0) {
        if (group.isMultiSelection) {
          // For multi-selection, add all default options
          const defaultValues = defaultOptions.map((option: any) => {
            if (group.isComboUpgrade && option._isCombo) {
              return {
                comboName: option.name,
                items: option._comboItems || [],
                price: option.price || 0,
              };
            }
            return option.name;
          });
          onVariantUpdate(selectedCardIndex, groupKey, defaultValues);
        } else {
          // For single selection, select the first default option
          const defaultOption = defaultOptions[0];
          const value =
            group.isComboUpgrade && defaultOption._isCombo
              ? {
                  comboName: defaultOption.name,
                  items: defaultOption._comboItems || [],
                  price: defaultOption.price || 0,
                }
              : defaultOption.name;
          onVariantUpdate(selectedCardIndex, groupKey, value);
        }
      }
    });
  }, [selectedCard?.id, hasNewVariants, variantGroups.length]);

  return (
    <div className="space-y-4">
      <h3
        className="text-lg font-semibold mb-4"
        style={{ color: theme?.textColor }}
      >
        {t("menu.customizationOptions")}
      </h3>

      {/* Quantity Input Field with +/- Buttons */}
      {selectedCard && (
        <div
          className="p-4 rounded-lg mb-6"
          style={{
            backgroundColor: `${theme?.primaryColor}10`,
            borderRadius: theme?.borderRadius,
          }}
        >
          <h4
            className="text-center font-medium mb-3"
            style={{ color: theme?.textColor }}
          >
            {t("menu.quantity")}
          </h4>
          <div className="flex items-center justify-center gap-3">
            {/* Minus Button */}
            <button
              onClick={handleQuantityDecrease}
              disabled={parseInt(quantityInput, 10) <= 1}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-colors shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme?.primaryColor || "#ea580c",
                color: "#fff",
                border: "none",
              }}
            >
              <FaMinus size={10} style={{ color: "#fff" }} />
            </button>

            {/* Quantity Display */}
            <span
              className="font-bold text-lg min-w-[40px] text-center"
              style={{ color: theme?.textColor }}
            >
              {quantityInput}
            </span>

            {/* Plus Button */}
            <button
              onClick={handleQuantityIncrease}
              className="w-8 h-8 rounded-full text-white flex items-center justify-center hover:opacity-80 transition-colors shadow-md cursor-pointer"
              style={{
                backgroundColor: theme?.primaryColor || "#ea580c",
              }}
            >
              <FaPlus size={10} style={{ color: "#fff" }} />
            </button>
          </div>
        </div>
      )}

      {/* Variant Options - Always Visible */}
      <div className="space-y-6">
        {/* Render new variants_and_options structure */}
        {hasNewVariants &&
          variantGroups.map((group, groupIndex) => {
            const groupKey = group.id;
            const groupOptions = Array.isArray(group.data) ? group.data : [];

            return (
              <div
                key={groupIndex}
                className="space-y-3 w-full px-0 md:px-2 lg:px-4 py-4 rounded-xl"
                style={{
                  marginBottom: "1.5rem",
                  boxShadow: "0 2px 8px -2px rgba(0,0,0,0.04)",
                  backgroundColor: theme?.cardColor,
                }}
              >
                {/* Group Header */}
                <div className="flex items-center justify-between mb-2">
                  <h4
                    className="text-lg font-semibold"
                    style={{ color: theme?.textColor }}
                  >
                    {group.formName}
                    {(group.isRequired || group.minSelection > 0) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                    {group.minSelection > 0 && (
                      <span
                        className={`ml-2 text-xs px-2 py-1 rounded-full ${
                          getSelectionCount(
                            selectedCard?.variants?.[groupKey],
                            group.isMultiSelection
                          ) >= group.minSelection
                            ? "bg-green-100 text-green-600"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {getSelectionCount(
                          selectedCard?.variants?.[groupKey],
                          group.isMultiSelection
                        )}
                        /{group.minSelection} min
                      </span>
                    )}
                  </h4>
                  {(group.isMultiSelection ||
                    group.minSelection > 0 ||
                    group.maxSelection > 1) && (
                    <span
                      className="text-sm px-3 py-1 rounded-full font-medium"
                      style={{
                        color: theme?.primaryColor,
                        backgroundColor: `${theme?.primaryColor}15`,
                      }}
                    >
                      {group.isMultiSelection &&
                      group.minSelection > 0 &&
                      group.maxSelection > group.minSelection
                        ? `Select ${group.minSelection}-${group.maxSelection}`
                        : group.isMultiSelection && group.maxSelection > 0
                        ? `Select up to ${group.maxSelection}`
                        : group.isMultiSelection
                        ? "Multiple Selection"
                        : group.minSelection > 0
                        ? "Required"
                        : "Optional"}
                    </span>
                  )}
                </div>

                {/* Group Options - Toters Style Inline Radio/Checkbox */}
                <div className="flex flex-col divide-y" style={{ borderColor: `${theme?.borderColor}30` }}>
                  {groupOptions.map((optionData: any, optionIndex: number) => {
                    const selected = selectedCard?.variants?.[groupKey];
                    const isSelected = group.isMultiSelection
                      ? Array.isArray(selected) &&
                        (selected.includes(optionData.name) ||
                          selected.some(
                            (item: any) =>
                              item &&
                              typeof item === "object" &&
                              item.comboName === optionData.name
                          ))
                      : selected === optionData.name ||
                        (selected &&
                          typeof selected === "object" &&
                          selected.comboName === optionData.name);

                    const selectedCount = getSelectionCount(
                      selected,
                      group.isMultiSelection
                    );

                    const canSelect = group.isMultiSelection
                      ? isSelected || selectedCount < group.maxSelection
                      : true;

                    // Skip inactive options
                    if (optionData.is_active === false) return null;

                    const optionPrice = Number(optionData.price || 0);

                    return (
                      <motion.div
                        key={optionIndex}
                        onClick={() => {
                          // Always allow clicking selected items to deselect
                          if (!canSelect && !isSelected) return;

                          if (group.isMultiSelection) {
                            const currentSelected = Array.isArray(selected)
                              ? selected
                              : [];
                            let newSelected;

                            if (isSelected) {
                              // Always allow deselection - remove minimum restriction
                              newSelected = currentSelected.filter(
                                (item: any) => {
                                  if (!item) {
                                    return false; // Remove null/undefined items
                                  }
                                  if (typeof item === "string") {
                                    return item !== optionData.name;
                                  } else if (
                                    typeof item === "object" &&
                                    item.comboName
                                  ) {
                                    return item.comboName !== optionData.name;
                                  }
                                  return true;
                                }
                              );
                            } else {
                              // Check if we can select (respect maximum selection)
                              if (
                                currentSelected.length >= group.maxSelection
                              ) {
                                return; // Cannot select if it would exceed maximum
                              }

                              // Add to selection
                              const valueToAdd =
                                group.isComboUpgrade && optionData._isCombo
                                  ? {
                                      comboName: optionData.name,
                                      items: optionData._comboItems || [],
                                      price: optionData.price || 0,
                                    }
                                  : optionData.name;
                              newSelected = [...currentSelected, valueToAdd];
                            }

                            onVariantUpdate(
                              selectedCardIndex,
                              groupKey,
                              newSelected
                            );
                          } else {
                            // Single selection - always allow toggle/deselection
                            const newValue = isSelected
                              ? null // Always allow deselection
                              : group.isComboUpgrade && optionData._isCombo
                              ? {
                                  comboName: optionData.name,
                                  items: optionData._comboItems || [],
                                  price: optionData.price || 0,
                                }
                              : optionData.name;
                            onVariantUpdate(
                              selectedCardIndex,
                              groupKey,
                              newValue
                            );
                          }
                        }}
                        className={`flex items-center gap-3 py-3.5 px-1 cursor-pointer transition-all duration-200 ${
                          !canSelect && !isSelected
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-gray-50 active:bg-gray-100"
                        }`}
                        style={{
                          borderColor: `${theme?.borderColor}20`,
                        }}
                        whileTap={
                          canSelect || isSelected ? { scale: 0.99 } : {}
                        }
                      >
                        {/* Radio/Checkbox indicator */}
                        <div className="flex-shrink-0">
                          {group.isMultiSelection ? (
                            // Checkbox style
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                                isSelected
                                  ? "border-transparent"
                                  : "border-gray-300"
                              }`}
                              style={{
                                backgroundColor: isSelected
                                  ? theme?.primaryColor
                                  : "transparent",
                              }}
                            >
                              {isSelected && (
                                <FaCheck className="text-white text-[10px]" />
                              )}
                            </div>
                          ) : (
                            // Radio button style
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                isSelected
                                  ? ""
                                  : "border-gray-300"
                              }`}
                              style={{
                                borderColor: isSelected
                                  ? theme?.primaryColor
                                  : undefined,
                              }}
                            >
                              {isSelected && (
                                <div
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{
                                    backgroundColor: theme?.primaryColor,
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </div>

                        {/* Option details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm ${isSelected ? "font-semibold" : "font-medium"}`}
                              style={{ color: theme?.textColor }}
                            >
                              {getLocalizedField(
                                optionData,
                                "name",
                                currentLanguage
                              )}
                            </span>
                          </div>
                          
                          {/* Note/Description */}
                          {getLocalizedField(
                            optionData,
                            "note",
                            currentLanguage
                          ) && (
                            <p
                              className="text-xs mt-0.5 opacity-60"
                              style={{ color: theme?.textColor }}
                            >
                              {getLocalizedField(
                                optionData,
                                "note",
                                currentLanguage
                              )}
                            </p>
                          )}

                          {/* Combo items preview */}
                          {group.isComboUpgrade &&
                            optionData._isCombo &&
                            optionData._comboItems && (
                              <p
                                className="text-xs mt-0.5 opacity-60"
                                style={{ color: theme?.textColor }}
                              >
                                {optionData._comboItems
                                  .map(
                                    (comboItem: any) =>
                                      `${comboItem.quantity || 1}x ${
                                        comboItem.item_name ||
                                        comboItem.name ||
                                        "Item"
                                      }`
                                  )
                                  .join(", ")}
                              </p>
                            )}
                        </div>

                        {/* Price */}
                        {optionPrice > 0 && (
                          <span
                            className="text-sm font-medium flex-shrink-0"
                            style={{ color: theme?.primaryColor }}
                          >
                            {group.isComboUpgrade ||
                            group.actionType?.toLowerCase() === "upgrade"
                              ? ""
                              : "+ "}
                            ${optionPrice.toFixed(2)}
                          </span>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
